import api from './api';

/**
 * TA Request Service
 * Handles faculty TA requests with named students
 */

/**
 * Get current faculty member's TA requests
 * @param {object} params - Query parameters
 * @param {string} params.term - Filter by term
 * @param {string} params.status - Filter by status
 */
export const getMyRequests = async (params = {}) => {
    const response = await api.get('/ta-request/mine', { params });
    return response.data;
};

/**
 * Get all TA requests (coordinator only)
 * @param {object} params - Query parameters
 * @param {string} params.term - Filter by term
 * @param {string} params.status - Filter by status
 * @param {string} params.priority - Filter by priority
 * @param {number} params.limit - Results limit
 * @param {number} params.offset - Results offset
 */
export const getAllRequests = async (params = {}) => {
    const response = await api.get('/ta-request', { params });
    // The server returns { requests, total, ... }. Unwrap to the requests
    // array: the TA coordinator dashboard treats this as an array and calls
    // .filter()/.length on it, mirroring the other coordinator services. If
    // the server's whole-term backstop cut the list off, the array also
    // carries truncated and the real total, as applicationService does; read
    // them off this array directly, since a copy drops them.
    const { requests, truncated, total } = response.data;
    if (truncated) Object.assign(requests, { truncated, total });
    return requests;
};

/**
 * Get a single TA request by ID
 * @param {string} requestId - Request ID
 */
export const getRequestById = async (requestId) => {
    const response = await api.get(`/ta-request/${requestId}`);
    return response.data;
};

/**
 * Create a new TA request (faculty only)
 * @param {object} data - Request data
 * @param {string} data.courseId - Course ID
 * @param {string} data.term - Academic term
 * @param {object} data.namedStudent - Named student info (required)
 * @param {string} data.namedStudent.name - Student name
 * @param {string} data.namedStudent.email - Student email
 * @param {string} data.namedStudent.studentId - Student ID (900 number)
 * @param {string} data.justification - Justification for request
 * @param {number} data.requestedHours - Hours per week
 * @param {string} data.preferredSchedule - Preferred schedule
 * @param {string[]} data.responsibilities - List of responsibilities
 * @param {string} data.priority - Priority level (normal, high, urgent)
 */
export const createRequest = async (data) => {
    const response = await api.post('/ta-request', data);
    return response.data;
};

/**
 * Update a TA request
 * @param {string} requestId - Request ID
 * @param {object} updates - Fields to update
 */
export const updateRequest = async (requestId, updates) => {
    const response = await api.put(`/ta-request/${requestId}`, updates);
    return response.data;
};

/**
 * Approve a TA request and create assignment (coordinator only)
 * @param {string} requestId - Request ID
 * @param {object} data - Approval details
 * @param {Date} data.startDate - Assignment start date
 * @param {Date} data.endDate - Assignment end date
 * @param {object} data.schedule - Schedule details
 * @param {string} data.notes - Notes for the approval
 */
export const approveRequest = async (requestId, data) => {
    const response = await api.post(`/ta-request/${requestId}/approve`, data);
    return response.data;
};

/**
 * Deny a TA request (coordinator only)
 * @param {string} requestId - Request ID
 * @param {string} reason - Reason for denial
 */
export const denyRequest = async (requestId, reason) => {
    const response = await api.post(`/ta-request/${requestId}/deny`, {
        reason,
    });
    return response.data;
};

/**
 * Cancel a TA request (faculty can cancel own)
 * @param {string} requestId - Request ID
 */
export const cancelRequest = async (requestId) => {
    const response = await api.post(`/ta-request/${requestId}/cancel`);
    return response.data;
};

/**
 * Get TA request statistics (coordinator only)
 * @param {string} term - Optional term filter
 */
export const getStats = async (term) => {
    const params = term ? { term } : {};
    const response = await api.get('/ta-request/stats', { params });
    return response.data;
};

export default {
    getMyRequests,
    getAllRequests,
    getRequestById,
    createRequest,
    updateRequest,
    approveRequest,
    denyRequest,
    cancelRequest,
    getStats,
};
